import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import axios from 'cccisd-axios';
import Modal from 'cccisd-modal';
import Tooltip from 'cccisd-tooltip';
import Table from 'cccisd-table';
import { useQuery, useQueryClient } from 'cccisd-react-query';
import { client } from 'cccisd-apollo';
import Toggle from 'cccisd-toggle';
import Confirm from 'cccisd-confirm';
import notification from 'cccisd-notification';
import { updateSessionDeploymentInfo } from 'js/assessment/helpers';
import ContactForm from './ContactForm';
import style from '../style.css';
import _pickBy from 'lodash/pickBy';
// Icons
import IconUser from 'cccisd-icons/user';
import IconEdit from 'cccisd-icons/pencil5';
import IconDelete from 'cccisd-icons/bin';
import IconPlus from 'cccisd-icons/plus-circle2';
// Queries
import contactsQuery from './contacts.graphql';

const Boilerplate = window.cccisd.boilerplate;
const Fortress = window.cccisd.fortress;

const contactNameMap = {
    caregiver: 'Caregiver',
    educator: 'Educator',
    mentalHealthProvider: 'Mental Health Provider',
};

const ManageReporters = props => {
    const { plan, studentName, setForceReloadQueries, surveyProgress, thereAreCompletedSurveys } = props;
    const [tableData, setTableData] = useState({});
    const queryClient = useQueryClient();

    const planSurveys = useMemo(() => {
        const surveys = {
            caregiver: [],
            educator: [],
            mentalHealthProvider: [],
        };

        plan.sessionList
            .filter(session => !session.archivedAt)
            .forEach(session => {
                session.settings.sessionCollection.forEach(survey => {
                    if (session.settings.reporter === 'Caregiver') {
                        surveys.caregiver.push(survey.label);
                    } else if (session.settings.reporter === 'Educator') {
                        surveys.educator.push(survey.label);
                    } else if (session.settings.reporter === 'Mental Health Provider') {
                        surveys.mentalHealthProvider.push(survey.label);
                    }
                });
            });

        return surveys;
    }, [plan]);

    const { data: contactsData, isLoading: contactsLoading } = useQuery({
        queryKey: ['contacts', plan?.selectedByPawn?.pawn?.pawnId],
        queryFn: async () => {
            const res = await client.query({
                query: contactsQuery,
                variables: {
                    studentPawnId: plan?.selectedByPawn?.pawn?.pawnId,
                    instructorPawnId: Fortress.user.acting.id,
                },
                fetchPolicy: 'network-only',
            });

            return res.data.roles.contactList;
        },
        enabled: !!plan?.selectedByPawn?.pawn?.pawnId,
        refetchOnWindowFocus: false,
    });

    const getProgress = contactId => {
        if (!surveyProgress) {
            return 'Not Started';
        }

        const match = Object.keys(surveyProgress).find(key => key.endsWith(contactId));
        if (!match) {
            return 'Not Started';
        }

        const progress = _pickBy(
            surveyProgress[match],
            (val, key) => key.startsWith('survey') && !key.endsWith('_lastVisit')
        );
        if (Object.values(progress).every(value => value === 2)) {
            return 'Completed';
        }

        return 'In Progress';
    };

    const parseTableData = () => {
        const data = {
            caregiver: [],
            educator: [],
            mentalHealthProvider: [],
        };

        if (contactsData && contactsData.length > 0) {
            const formatTableData = contact => {
                const session = plan.sessionList.find(sess => {
                    return sess.settings.reporter === contact.fields.contactType;
                });

                let contactSessionDeploymentInfo = {};

                if (session) {
                    contactSessionDeploymentInfo = session.sessionDeploymentInfo?.[contact.pawn.pawnId];
                }

                return {
                    id: contact.id,
                    name: contact.fields.contactName,
                    email: contact.fields.contactEmail,
                    assigned: contactSessionDeploymentInfo?.assigned || false,
                    progress: getProgress(contact.id) || 'Not Started',
                    timesAssessed: contact.selectedAssignmentPlanCount || 0,
                    pawn: contact.pawn,
                    // progress: contact.fields.progress || 'N/A',
                };
            };

            contactsData.forEach(contact => {
                if (contact.fields.contactType === 'Caregiver') {
                    data.caregiver.push(formatTableData(contact));
                } else if (contact.fields.contactType === 'Educator') {
                    data.educator.push(formatTableData(contact));
                } else if (contact.fields.contactType === 'Mental Health Provider') {
                    data.mentalHealthProvider.push(formatTableData(contact));
                }
            });
        }

        return data;
    };

    useEffect(() => {
        if (contactsData && !contactsLoading) {
            const formattedData = parseTableData();
            setTableData(formattedData);
        }
    }, [contactsData]);

    const handleAssignedToggle = async (currentValue, contactPawn, session) => {
        const response = await updateSessionDeploymentInfo(
            [
                {
                    pawnId: contactPawn.pawnId,
                    respondentHash: contactPawn.respondentHash,
                    assigned: !currentValue,
                },
            ],
            { ...session, assignmentPlanId: plan.assignmentPlanId },
            'edit'
        );

        if (response?.data?.status === 'success') {
            queryClient.invalidateQueries(['contacts', plan?.selectedByPawn?.pawn?.pawnId]);
            queryClient.invalidateQueries(['getPlanSessions', plan?.assignmentPlanId]);
        } else {
            notification({ message: 'An error occurred updating assigned status', type: 'danger' });
        }
    };

    const refreshContactsData = () => queryClient.invalidateQueries(['contacts', plan?.selectedByPawn?.pawn?.pawnId]);
    const refreshPlanData = () => queryClient.invalidateQueries(['getPlanSessions', plan?.assignmentPlanId]);

    const handleDeleteContact = async (
        contact = { pawn: { pawnId: null, pawnHash: null }, name: '' },
        session,
        closeModal
    ) => {
        Confirm({
            message: 'Confirm Delete',
            description: `Are you sure you want to delete ${contact.name}?`,
            confirmLabel: 'Delete',
            abortLabel: 'Cancel',
        })
            .then(async () => {
                try {
                    const response = await axios.delete(
                        Boilerplate.route('api.assessment.removePawnFromPlans', {
                            pawnId: contact.pawn.pawnId,
                        })
                    );

                    if (response?.data?.status === 'success') {
                        if (response.data.data.pawn.deleted) {
                            notification({ message: 'Contact deleted successfully', type: 'success' });
                        } else {
                            notification({
                                message: `Contact removed from ${
                                    response.data.data.removed_from_plans_count ?? ''
                                } assessment(s)`,
                                type: 'success',
                            });
                        }
                        refreshContactsData();
                        refreshPlanData();
                    } else {
                        notification({ message: 'An error occured when deleting.', type: 'danger' });
                    }
                } catch (e) {
                    console.error(e);
                }
            })
            .fail(() => {
                // nothing
            });
    };

    if (contactsLoading) {
        return (
            <button className="btn btn-secondary" type="button" style={{ marginTop: '0.5em' }} disabled>
                <IconUser /> Manage Reporters
            </button>
        );
    }

    return (
        <Modal
            title={`Edit or add reporters for ${studentName}.`}
            trigger={
                <Tooltip title={`Edit or add reporters for ${studentName}.`}>
                    <button className="btn btn-secondary" type="button" style={{ marginTop: '0.5em' }}>
                        <IconUser /> Manage Reporters
                    </button>
                </Tooltip>
            }
            beforeClose={() => {
                setForceReloadQueries();
            }}
            size="large"
            render={({ closeModal }) => {
                return (
                    <div className={style.manageReportersWrapper}>
                        {!contactsLoading && (
                            <div>
                                {thereAreCompletedSurveys && (
                                    <div className="alert alert-warning" role="alert">
                                        Manage actions are limited because data collection has begun for these
                                        assessments.
                                    </div>
                                )}
                                {Object.keys(planSurveys).map(key => {
                                    if (planSurveys[key].length === 0) {
                                        return null;
                                    }

                                    return (
                                        <div key={key} className="card mb-4">
                                            <div className="card-header">
                                                <div>Assessments to be completed for {studentName}:</div>
                                                <ul>
                                                    {planSurveys[key].map((survey, index) => (
                                                        <li key={index}>
                                                            <strong>{survey}</strong>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <div className="card-body">
                                                <h4>Assign {contactNameMap[key]}s</h4>
                                                <Table
                                                    columns={[
                                                        {
                                                            name: 'name',
                                                            label: 'Name',
                                                            render: (value, row) => {
                                                                return <div>{row.name ?? <em>Not Provided</em>}</div>;
                                                            },
                                                        },
                                                        {
                                                            name: 'email',
                                                            label: 'Email',
                                                            render: (value, row) => {
                                                                return <div>{row.email ?? <em>Not Provided</em>}</div>;
                                                            },
                                                        },
                                                        {
                                                            name: 'assigned',
                                                            label: 'Assigned',
                                                            render: (value, row) => {
                                                                const session = plan.sessionList.find(
                                                                    sess =>
                                                                        sess.settings.reporter === contactNameMap[key]
                                                                );
                                                                return (
                                                                    <div>
                                                                        <Toggle
                                                                            onChange={() =>
                                                                                handleAssignedToggle(
                                                                                    row.assigned,
                                                                                    row.pawn,
                                                                                    session
                                                                                )
                                                                            }
                                                                            value={row.assigned}
                                                                            disabled={
                                                                                row.progress !== 'Not Started' ||
                                                                                thereAreCompletedSurveys
                                                                            }
                                                                        />
                                                                    </div>
                                                                );
                                                            },
                                                        },
                                                        {
                                                            name: 'progress',
                                                            label: 'Progress',
                                                        },
                                                        {
                                                            name: 'timesAssessed',
                                                            label: 'Times Assessed',
                                                        },
                                                        {
                                                            name: 'actions',
                                                            label: 'Actions',
                                                            render: (value, row) => {
                                                                const session = plan.sessionList.find(
                                                                    sess =>
                                                                        sess.settings.reporter === contactNameMap[key]
                                                                );
                                                                const contactHasSurveyProgress =
                                                                    row.progress !== 'Not Started' ||
                                                                    row.timesAssessed > 0;
                                                                return (
                                                                    <div>
                                                                        <Modal
                                                                            title={`Edit ${contactNameMap[key]}`}
                                                                            trigger={
                                                                                <Tooltip title="Edit reporter's information.">
                                                                                    <button
                                                                                        className="btn btn-success btn-sm me-2"
                                                                                        type="button"
                                                                                    >
                                                                                        <IconEdit />
                                                                                    </button>
                                                                                </Tooltip>
                                                                            }
                                                                            render={({
                                                                                closeModal: closeAddContactModal,
                                                                            }) => (
                                                                                <div>
                                                                                    <ContactForm
                                                                                        action="edit"
                                                                                        contactType={
                                                                                            contactNameMap[key]
                                                                                        }
                                                                                        closeModal={
                                                                                            closeAddContactModal
                                                                                        }
                                                                                        pawn={row.pawn}
                                                                                        initialValues={{
                                                                                            name: row.name,
                                                                                            email: row.email,
                                                                                        }}
                                                                                        refreshContactsData={
                                                                                            refreshContactsData
                                                                                        }
                                                                                        refreshPlanData={
                                                                                            refreshPlanData
                                                                                        }
                                                                                        contactHasSurveyProgress={
                                                                                            contactHasSurveyProgress
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        />
                                                                        {tableData[key].length !== 1 && ( // dont show delete action for last remaining contact
                                                                            <Tooltip
                                                                                title={
                                                                                    contactHasSurveyProgress ||
                                                                                    thereAreCompletedSurveys
                                                                                        ? 'Cannot delete contact with existing assessment progress or when data collection has begun for these assessments.'
                                                                                        : 'Delete'
                                                                                }
                                                                            >
                                                                                <button
                                                                                    className="btn btn-danger btn-sm"
                                                                                    type="button"
                                                                                    disabled={
                                                                                        contactHasSurveyProgress ||
                                                                                        thereAreCompletedSurveys
                                                                                    }
                                                                                    onClick={() =>
                                                                                        handleDeleteContact(
                                                                                            {
                                                                                                pawn: row.pawn,
                                                                                                name: row.name,
                                                                                            },
                                                                                            session,
                                                                                            closeModal
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <IconDelete />
                                                                                </button>
                                                                            </Tooltip>
                                                                        )}
                                                                    </div>
                                                                );
                                                            },
                                                        },
                                                    ]}
                                                    data={tableData[key]}
                                                    className="table"
                                                    showPerPageOptions={false}
                                                    hideShowingRowsSummary
                                                />
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'flex-end',
                                                        marginTop: '1em',
                                                    }}
                                                >
                                                    <Modal
                                                        title={`Add ${contactNameMap[key]}`}
                                                        trigger={
                                                            <button
                                                                className="btn btn-primary"
                                                                type="button"
                                                                disabled={thereAreCompletedSurveys}
                                                            >
                                                                <IconPlus /> Add {contactNameMap[key]}
                                                            </button>
                                                        }
                                                        render={({ closeModal: closeAddContactModal }) => (
                                                            <div>
                                                                <ContactForm
                                                                    action="add"
                                                                    contactType={contactNameMap[key]}
                                                                    closeModal={closeAddContactModal}
                                                                    session={plan.sessionList.find(
                                                                        sess =>
                                                                            sess.settings.reporter ===
                                                                            contactNameMap[key]
                                                                    )}
                                                                    plan={plan}
                                                                    refreshContactsData={refreshContactsData}
                                                                    refreshPlanData={refreshPlanData}
                                                                />
                                                            </div>
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <hr />
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                marginTop: '1em',
                            }}
                        >
                            <button className="btn btn-primary" type="button" onClick={closeModal}>
                                Done
                            </button>
                        </div>
                    </div>
                );
            }}
        />
    );
};

ManageReporters.propTypes = {
    plan: PropTypes.object,
    studentName: PropTypes.string,
    setForceReloadQueries: PropTypes.func,
    surveyProgress: PropTypes.object,
    thereAreCompletedSurveys: PropTypes.bool,
};

ManageReporters.defaultProps = {};

export default ManageReporters;
